/**
 * Centralized API client for authenticated requests
 * Provides consistent token retrieval, validation, and error handling
 */

interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

/**
 * Get authentication token from localStorage
 * @returns Token string or null if not found
 */
export function getAuthToken(): string | null {
  const token = localStorage.getItem('token');
  if (!token) {
    return null;
  }

  // Basic token validation - check if it's not empty and looks like a JWT
  if (token.trim() === '' || token.length < 10) {
    console.warn('Invalid token format detected');
    localStorage.removeItem('token'); // Remove invalid token
    return null;
  }

  return token;
}

/**
 * Check if user is authenticated
 * @returns true if valid token exists
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

/**
 * Make an authenticated API request
 * @param endpoint - API endpoint path (e.g., '/api/admin/health')
 * @param options - Fetch options
 * @returns Response data or throws error
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Get authentication token
  const token = getAuthToken();

  if (!token) {
    const error: ApiError = {
      message: 'Authentication token not found. Please log in.',
      code: 'NO_TOKEN'
    };
    throw error;
  }

  try {
    // Prepare headers with authentication
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
      Authorization: `Bearer ${token}`
    };

    // Make request
    const response = await fetch(endpoint, {
      ...options,
      headers
    });

    // Handle unauthorized responses
    if (response.status === 401) {
      // Token might be expired or invalid
      localStorage.removeItem('token'); // Clear invalid token

      const error: ApiError = {
        message: 'Authentication failed. Please log in again.',
        status: 401,
        code: 'UNAUTHORIZED'
      };
      throw error;
    }

    // Handle forbidden responses
    if (response.status === 403) {
      const error: ApiError = {
        message: 'Access denied. You do not have permission to access this resource.',
        status: 403,
        code: 'FORBIDDEN'
      };
      throw error;
    }

    // Handle other error responses
    if (!response.ok) {
      const error: ApiError = {
        message: `Request failed with status ${response.status}`,
        status: response.status,
        code: 'HTTP_ERROR'
      };
      throw error;
    }

    // Parse and return response
    const data = await response.json();
    return data as T;
  } catch (error) {
    // Re-throw API errors
    if (isApiError(error)) {
      throw error;
    }

    // Handle network errors
    const apiError: ApiError = {
      message: error instanceof Error ? error.message : 'Network error occurred',
      code: 'NETWORK_ERROR'
    };
    throw apiError;
  }
}

/**
 * Type guard for ApiError
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as ApiError).message === 'string'
  );
}

/**
 * Make a GET request
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' });
}

/**
 * Make a POST request
 */
export async function apiPost<T>(
  endpoint: string,
  data: unknown
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Make a PUT request
 */
export async function apiPut<T>(
  endpoint: string,
  data: unknown
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

/**
 * Make a DELETE request
 */
export async function apiDelete<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'DELETE' });
}
